
import type { ChargeRaw, Unit, Owner, Vehicle, WaterReading, Adjustment, AllData, PaymentStatus, TariffParking, OtherFeeConfig } from '../types';
import { UnitType, VehicleTier, ParkingTariffTier } from '../types';
import { getPreviousPeriod, roundVND } from '../utils/helpers';

interface CalculationInput {
    unit: Unit;
    owner: Owner;
    vehicles: Vehicle[];
    adjustments: Adjustment[];
}

const money = (n: number) => Math.max(0, Math.round(n));
const applyVAT = (net: number, vatRate: number) => {
    const v = net * (vatRate / 100);
    return { net: money(net), vat: money(v), gross: money(net + v) };
};

const isBusinessUnit = (unit: Unit) => unit.UnitType === UnitType.KIOS || unit.Status === 'Business';

/**
 * Lấy chỉ số tiêu thụ nước đã được lưu trữ cho một kỳ nhất định.
 */
const getWaterUsage = (unitId: string, readingPeriod: string, allWaterReadings: WaterReading[]): number => {
    const readingForThisPeriod = allWaterReadings.find(r => r.UnitID === unitId && r.Period === readingPeriod);
    return readingForThisPeriod?.consumption ?? 0;
};

const calcServiceFee = (unit: Unit, tariffs: AllData['tariffs']) => {
    const tariffKey = unit.UnitType === UnitType.KIOS ? 'KIOS' 
                    : unit.Status === 'Business' ? 'Business Apartment' 
                    : 'Apartment';
    
    const tariff = tariffs.service.find(t => t.LoaiHinh === tariffKey);
    if (!tariff) return { net: 0, vat: 0, gross: 0 };
    
    const net = unit.Area_m2 * tariff.ServiceFee_per_m2;
    return applyVAT(net, tariff.VAT_percent);
};

const findParkingTariff = (parkingTariffs: TariffParking[], tier: ParkingTariffTier) => {
    // So khớp không phân biệt hoa thường để tăng tính chịu lỗi của hệ thống
    return parkingTariffs.find(t => t.Tier.toLowerCase() === tier.toLowerCase());
};

const calcVehicleFee = (vehicles: Vehicle[], period: string, tariffs: AllData['tariffs']) => {
    const endOfMonth = new Date(new Date(period + '-02').getFullYear(), new Date(period + '-02').getMonth() + 1, 0);
    
    // Loại trừ các xe đang ở trạng thái 'Xếp lốt' khỏi tính phí
    // Thêm kiểm tra StartDate: nếu không có StartDate thì mặc định là tính phí (nếu isActive)
    const activeVehicles = vehicles.filter(v => {
        const isActive = v.isActive !== false; // Mặc định là true nếu không có trường này
        const isNotWaiting = v.parkingStatus !== 'Xếp lốt';
        
        // Kiểm tra StartDate: nếu không có StartDate thì mặc định là tính phí (nếu isActive)
        let hasStarted = true;
        if (v.StartDate) {
            try {
                const startDate = new Date(v.StartDate);
                if (!isNaN(startDate.getTime())) {
                    hasStarted = startDate <= endOfMonth;
                }
            } catch (e) {
                // Nếu lỗi parse date thì mặc định là đã bắt đầu để tránh mất phí
                hasStarted = true;
            }
        }
        
        return isActive && isNotWaiting && hasStarted;
    });

    // Hàm phụ để kiểm tra loại xe linh hoạt hơn (chấp nhận cả label tiếng Việt)
    const isType = (v: Vehicle, type: VehicleTier) => {
        const t = (v.Type || '').toLowerCase().trim();
        const target = type.toLowerCase();
        if (t === target) return true;
        
        // Kiểm tra theo label tiếng Việt và các biến thể linh hoạt hơn
        if (target === 'car') {
            return (t === 'ô tô' || t === 'oto' || t === 'o to' || t === 'xe con') || 
                   (t.includes('ô tô') && !t.includes(' a') && !t.includes('- a') && !t.includes('(a)'));
        }
        if (target === 'car_a') {
            return t === 'ô tô - a' || t === 'oto a' || t === 'oto (a)' || 
                   ((t.includes('ô tô') || t.includes('oto')) && (t.includes(' a') || t.includes('- a') || t.includes('(a)')));
        }
        if (target === 'motorbike') {
            return (t === 'xe máy' || t === 'xe may' || t === 'mô tô' || t === 'moto') || 
                   ((t.includes('xe máy') || t.includes('xe may')) && !t.includes('điện') && !t.includes('dien'));
        }
        if (target === 'ebike') {
            return t === 'xe điện' || t === 'xe dien' || t === 'xe đạp điện' || t === 'xe dap dien' ||
                   ((t.includes('xe máy') || t.includes('xe may') || t.includes('xe điện') || t.includes('xe dien') || t.includes('xe đạp')) && (t.includes('điện') || t.includes('dien')));
        }
        if (target === 'bicycle') {
            return (t === 'xe đạp' || t === 'xe dap') || 
                   ((t.includes('xe đạp') || t.includes('xe dap')) && !t.includes('điện') && !t.includes('dien'));
        }
        
        return false;
    };

    // Đối với ô tô, chỉ tính phí nếu có lốt (Lốt chính hoặc Lốt tạm)
    // Đối với các loại xe khác, luôn tính phí nếu active
    const billableVehicles = activeVehicles.filter(v => {
        const isCar = isType(v, VehicleTier.CAR) || isType(v, VehicleTier.CAR_A);
        if (isCar) {
            return v.parkingStatus === 'Lốt chính' || v.parkingStatus === 'Lốt tạm';
        }
        return true;
    });
    
    const carCount = billableVehicles.filter(v => isType(v, VehicleTier.CAR)).length;
    const carACount = billableVehicles.filter(v => isType(v, VehicleTier.CAR_A)).length;
    const motoCount = billableVehicles.filter(v => 
        isType(v, VehicleTier.MOTORBIKE) || 
        isType(v, VehicleTier.EBIKE)
    ).length;
    const bicycleCount = billableVehicles.filter(v => isType(v, VehicleTier.BICYCLE)).length;
    
    const carTariff = findParkingTariff(tariffs.parking, ParkingTariffTier.CAR);
    const carATariff = findParkingTariff(tariffs.parking, ParkingTariffTier.CAR_A);
    const moto12Tariff = findParkingTariff(tariffs.parking, ParkingTariffTier.MOTO12);
    const moto34Tariff = findParkingTariff(tariffs.parking, ParkingTariffTier.MOTO34);
    const bicycleTariff = findParkingTariff(tariffs.parking, ParkingTariffTier.BICYCLE);

    const missing = [];
    if (!carTariff) missing.push(ParkingTariffTier.CAR);
    if (!carATariff) missing.push(ParkingTariffTier.CAR_A);
    if (!moto12Tariff) missing.push(ParkingTariffTier.MOTO12);
    if (!moto34Tariff) missing.push(ParkingTariffTier.MOTO34);
    if (!bicycleTariff) missing.push(ParkingTariffTier.BICYCLE);

    if (missing.length > 0) {
         console.warn(`Cảnh báo: Cấu hình đơn giá gửi xe chưa đầy đủ! Thiếu: ${missing.join(', ')}`);
         if (vehicles.length > 0) {
            console.log(`Đang tính phí gửi xe cho căn hộ ${vehicles[0].UnitID} (${vehicles.length} xe)`);
         }
    }

    // Debug đặc biệt cho căn hộ 2120
    if (vehicles.length > 0 && vehicles[0].UnitID === '2120') {
        console.log('--- DEBUG UNIT 2120 PARKING FEE ---');
        console.log('All Vehicles:', vehicles);
        console.log('Active Vehicles:', activeVehicles);
        console.log('Billable Vehicles:', billableVehicles);
        console.log('Counts:', { carCount, carACount, motoCount, bicycleCount });
        console.log('Tariffs Found:', { carTariff: !!carTariff, carATariff: !!carATariff, moto12Tariff: !!moto12Tariff, moto34Tariff: !!moto34Tariff, bicycleTariff: !!bicycleTariff });
    }

    let net = 0;
    if (carCount > 0 && carTariff) net += carCount * carTariff.Price_per_unit;
    if (carACount > 0 && carATariff) net += carACount * carATariff.Price_per_unit;
    
    // Tính phí lũy tiến cho xe máy
    if (motoCount > 0) {
        if (moto12Tariff) net += Math.min(2, motoCount) * moto12Tariff.Price_per_unit;
        if (moto34Tariff) net += Math.max(0, motoCount - 2) * moto34Tariff.Price_per_unit;
    }
    
    if (bicycleCount > 0 && bicycleTariff) net += bicycleCount * bicycleTariff.Price_per_unit;
    
    // Sử dụng VAT của lốt ô tô làm mặc định, nếu không có lấy 8%
    const vatPercent = carTariff?.VAT_percent ?? 8;
    return {
        counts: { car: carCount, carA: carACount, motoTotal: motoCount, bicycle: bicycleCount },
        ...applyVAT(net, vatPercent)
    };
};

const calcWaterFee = (unit: Unit, period: string, allData: AllData) => {
    const usage = getWaterUsage(unit.UnitID, period, allData.waterReadings);
    
    if (usage <= 0) return { usage, ...applyVAT(0, 0) };

    const sortedTiers = [...allData.tariffs.water].sort((a, b) => a.From_m3 - b.From_m3);
    
    if (isBusinessUnit(unit)) {
        const businessTariff = sortedTiers.find(t => t.To_m3 === null);
        if (!businessTariff) return { usage, ...applyVAT(0, 0) };
        const taxed = applyVAT(usage * businessTariff.UnitPrice, businessTariff.VAT_percent);
        return { usage, ...taxed };
    }

    let net = 0;
    let consumptionLeft = usage;
    let lastTierEnd = 0;

    for (const tier of sortedTiers) {
        if (consumptionLeft <= 0) break;
        const tierEnd = tier.To_m3 ?? Infinity;
        const usageInTier = Math.min(consumptionLeft, tierEnd - lastTierEnd);
        
        if (usageInTier > 0) {
            net += usageInTier * tier.UnitPrice;
        }
        
        consumptionLeft -= usageInTier;
        lastTierEnd = tierEnd === Infinity ? lastTierEnd + usageInTier : tierEnd;
    }
    
    const vatPercent = sortedTiers[0]?.VAT_percent ?? 5;
    return { usage, ...applyVAT(net, vatPercent) };
};

const calcOtherFees = (unit: Unit, otherFeeConfigs: OtherFeeConfig[]) => {
    const activeFees = (otherFeeConfigs || []).filter(f => f.isActive);
    const applicableFees = activeFees.filter(f => {
        if (f.targetType === 'all') return true;
        return (f.targetUnitIds || []).includes(unit.UnitID);
    });

    let total = 0;
    const details: { name: string, amount: number }[] = [];

    applicableFees.forEach(fee => {
        let amount = 0;
        if (fee.type === 'per_unit') {
            amount = fee.unitPrice;
        } else if (fee.type === 'per_m2') {
            amount = fee.unitPrice * unit.Area_m2;
        }
        total += amount;
        details.push({ name: fee.name, amount });
    });

    return { total: money(total), details };
};

export const calculateChargesBatch = async (
    period: string,
    calculationInputs: CalculationInput[],
    allData: AllData
): Promise<Omit<ChargeRaw, 'CreatedAt' | 'Locked' | 'isPrinted' | 'isSent'>[]> => {
    
    const rawResults = calculationInputs.map(input => {
        const { unit, owner, vehicles, adjustments } = input;
        
        const serviceFee = calcServiceFee(unit, allData.tariffs);
        const parkingFee = calcVehicleFee(vehicles, period, allData.tariffs);
        const waterFee = calcWaterFee(unit, period, allData);
        const otherFees = calcOtherFees(unit, allData.otherFeeConfigs);
        
        const adjustmentsTotal = adjustments.reduce((acc, adj) => acc + adj.Amount, 0);
        const totalDue = serviceFee.gross + parkingFee.gross + waterFee.gross + otherFees.total + adjustmentsTotal;

        return {
            Period: period,
            UnitID: unit.UnitID,
            OwnerName: owner.OwnerName,
            Phone: owner.Phone,
            Email: owner.Email,
            Area_m2: unit.Area_m2,
            ServiceFee_Base: serviceFee.net,
            ServiceFee_VAT: serviceFee.vat,
            ServiceFee_Total: serviceFee.gross,
            '#CAR': parkingFee.counts.car,
            '#CAR_A': parkingFee.counts.carA,
            '#MOTORBIKE': parkingFee.counts.motoTotal,
            '#BICYCLE': parkingFee.counts.bicycle,
            ParkingFee_Base: parkingFee.net,
            ParkingFee_VAT: parkingFee.vat,
            ParkingFee_Total: parkingFee.gross,
            Water_m3: waterFee.usage,
            WaterFee_Base: waterFee.net,
            WaterFee_VAT: waterFee.vat,
            WaterFee_Total: waterFee.gross,
            OtherFees_Total: otherFees.total,
            OtherFees_Detail: otherFees.details,
            Adjustments: adjustmentsTotal,
            TotalDue: money(totalDue),
            TotalPaid: 0,
            PaymentConfirmed: false,
            paymentStatus: 'pending' as PaymentStatus,
            ParentUnitID: unit.ParentUnitID || null,
        };
    });

    // Aggregate fees for grouped units
    const finalResultsMap = new Map<string, any>();
    
    // First pass: Initialize all units in the map
    rawResults.forEach(res => {
        finalResultsMap.set(res.UnitID, { ...res, SubUnits: [] });
    });

    // Second pass: Aggregate sub-unit fees into main units
    rawResults.forEach(res => {
        if (res.ParentUnitID && finalResultsMap.has(res.ParentUnitID)) {
            const mainUnit = finalResultsMap.get(res.ParentUnitID);
            const subUnit = finalResultsMap.get(res.UnitID);

            // Add sub-unit fees to main unit
            mainUnit.ServiceFee_Base += subUnit.ServiceFee_Base;
            mainUnit.ServiceFee_VAT += subUnit.ServiceFee_VAT;
            mainUnit.ServiceFee_Total += subUnit.ServiceFee_Total;
            mainUnit.ParkingFee_Base += subUnit.ParkingFee_Base;
            mainUnit.ParkingFee_VAT += subUnit.ParkingFee_VAT;
            mainUnit.ParkingFee_Total += subUnit.ParkingFee_Total;
            mainUnit.WaterFee_Base += subUnit.WaterFee_Base;
            mainUnit.WaterFee_VAT += subUnit.WaterFee_VAT;
            mainUnit.WaterFee_Total += subUnit.WaterFee_Total;
            mainUnit.OtherFees_Total += subUnit.OtherFees_Total;
            mainUnit.Adjustments += subUnit.Adjustments;
            mainUnit.TotalDue += subUnit.TotalDue;
            
            // Aggregate vehicle counts
            mainUnit['#CAR'] += subUnit['#CAR'];
            mainUnit['#CAR_A'] += subUnit['#CAR_A'];
            mainUnit['#MOTORBIKE'] += subUnit['#MOTORBIKE'];
            mainUnit['#BICYCLE'] += subUnit['#BICYCLE'];

            // Aggregate other fees details
            if (subUnit.OtherFees_Detail) {
                mainUnit.OtherFees_Detail = [...(mainUnit.OtherFees_Detail || []), ...subUnit.OtherFees_Detail];
            }

            // Track sub-units in main unit
            if (!mainUnit.SubUnits) mainUnit.SubUnits = [];
            mainUnit.SubUnits.push(subUnit.UnitID);

            // Reset sub-unit fees to 0 as they are now part of the main unit
            subUnit.ServiceFee_Base = 0;
            subUnit.ServiceFee_VAT = 0;
            subUnit.ServiceFee_Total = 0;
            subUnit.ParkingFee_Base = 0;
            subUnit.ParkingFee_VAT = 0;
            subUnit.ParkingFee_Total = 0;
            subUnit.WaterFee_Base = 0;
            subUnit.WaterFee_VAT = 0;
            subUnit.WaterFee_Total = 0;
            subUnit.OtherFees_Total = 0;
            subUnit.Adjustments = 0;
            subUnit.TotalDue = 0;
            subUnit['#CAR'] = 0;
            subUnit['#CAR_A'] = 0;
            subUnit['#MOTORBIKE'] = 0;
            subUnit['#BICYCLE'] = 0;
            subUnit.OtherFees_Detail = [];
        }
    });
    
    return Promise.resolve(Array.from(finalResultsMap.values()).map(res => ({
        ...res,
        TotalDue: roundVND(res.TotalDue)
    })));
};
